import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

import { API_PATHS } from '../config/api-paths';
import { RatingScaleApi } from '../tokens/rating-scale.token';
import { RatingTier } from '../models/rating-scale.model';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-only `RatingScaleApi` (EM-1466, epic EM-1447 Story 2) —
 * `GET /services/embedded/api/financing/rating-scale`.
 *
 * Follows `RoleMatrixService` exactly (Rule 17 — reuse the caching shape, do not invent a second
 * one): the in-flight observable is `shareReplay`-ed and the resolved value held in a signal, so N
 * callers in a session cause exactly **one** HTTP call. A failure is not cached, so a later attempt
 * can still succeed.
 *
 * **No compiled-in fallback tier table.** On failure `tiers()` stays empty and `error()` carries the
 * backend's message; the grade legend renders that message instead of fabricated bands (Rule 09).
 *
 * Rules 07 / 10 — no mock fallback, no `Mock*` sibling. Playwright intercepts with `page.route()`.
 */
@Injectable()
export class RatingScaleService implements RatingScaleApi {
  private readonly http = inject(HttpClient);

  private readonly tiersSig = signal<readonly RatingTier[]>([]);
  private readonly errorSig = signal<string | null>(null);

  /** The cached in-flight/resolved request, so N callers cause exactly one HTTP call. */
  private inFlight: Observable<readonly RatingTier[]> | null = null;

  readonly tiers = this.tiersSig.asReadonly();
  readonly error = this.errorSig.asReadonly();

  /**
   * The tier for ONE grade — the single lookup every surface uses to answer "what band / level /
   * PD range / platform action does this grade carry?".
   *
   * Returns `null` when the grade is blank/unknown or the scale has not loaded yet; the caller
   * renders an em dash (and, where relevant, the `error()` banner) rather than falling back to a
   * compiled-in band table.
   */
  tierFor(grade: string | null | undefined): RatingTier | null {
    const code = (grade ?? '').trim().toUpperCase();
    if (!code) return null;
    return this.tiersSig().find(tier => tier.grade === code) ?? null;
  }

  load(): Observable<readonly RatingTier[]> {
    if (this.tiersSig().length > 0) {
      return of(this.tiersSig());
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.http.get<RatingTier[]>(API_PATHS.FINANCING_RATING_SCALE).pipe(
      tap((tiers) => {
        this.tiersSig.set(tiers ?? []);
        this.errorSig.set(null);
      }),
      catchError((err: HttpErrorResponse) => {
        // Rule 09 — keep the backend's own reason; the caller renders this string directly.
        this.errorSig.set(extractBackendErrorMessage(err, 'Could not load the rating scale.'));
        // Allow a later retry rather than caching the failure forever.
        this.inFlight = null;
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlight;
  }
}
